import { useEffect, useState } from 'react'
import * as Toast from '@radix-ui/react-toast'
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react'
import { cn } from '@/lib/cn'

type Variant = 'success' | 'error' | 'info'

type ToastItem = {
  id: number
  title: string
  description?: string
  variant: Variant
  duration?: number
}

// Minimal pub/sub so `toast(...)` can be called from any handler — pages,
// hooks, mutation onError — without threading a context down. The Toaster
// component subscribes and renders.
type Listener = (t: ToastItem) => void
let listeners: Listener[] = []
let nextId = 1

function emit(item: Omit<ToastItem, 'id'>): void {
  const full = { id: nextId++, ...item }
  for (const l of listeners) l(full)
}

export const toast = {
  success: (title: string, description?: string) => emit({ title, description, variant: 'success' }),
  error:   (title: string, description?: string) => emit({ title, description, variant: 'error' }),
  info:    (title: string, description?: string, opts?: { duration?: number }) =>
    emit({ title, description, variant: 'info', duration: opts?.duration }),
}

const VARIANT_STYLE: Record<Variant, { bar: string; icon: React.ReactNode }> = {
  success: { bar: 'border-emerald-200 bg-emerald-50', icon: <CheckCircle2 size={14} className="text-emerald-600" /> },
  error:   { bar: 'border-red-200 bg-red-50',         icon: <AlertCircle  size={14} className="text-red-600"  /> },
  info:    { bar: 'border-slate-200 bg-white',        icon: <Info         size={14} className="text-penser-blue-dark" /> },
}

export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>([])

  useEffect(() => {
    const sub: Listener = (t) => setItems((prev) => [...prev, t])
    listeners.push(sub)
    return () => { listeners = listeners.filter((l) => l !== sub) }
  }, [])

  function dismiss(id: number) {
    setItems((prev) => prev.filter((t) => t.id !== id))
  }

  return (
    <Toast.Provider swipeDirection="right" duration={5000}>
      {items.map((t) => {
        const { bar, icon } = VARIANT_STYLE[t.variant]
        return (
          <Toast.Root
            key={t.id}
            open
            duration={t.duration}
            onOpenChange={(open) => !open && dismiss(t.id)}
            className={cn(
              'rounded-md border px-3 py-2 shadow-panel data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-right-full data-[state=closed]:slide-out-to-right-full',
              bar,
            )}
          >
            <div className="flex items-start gap-2">
              <div className="mt-0.5 shrink-0">{icon}</div>
              <div className="min-w-0 flex-1">
                <Toast.Title className="text-xs font-semibold text-slate-800">{t.title}</Toast.Title>
                {t.description && (
                  <Toast.Description className="mt-0.5 text-[11px] text-slate-600">
                    {t.description}
                  </Toast.Description>
                )}
              </div>
              <Toast.Close className="penser-hit rounded p-0.5 text-slate-400 hover:text-slate-700" aria-label="Close">
                <X size={11} />
              </Toast.Close>
            </div>
          </Toast.Root>
        )
      })}
      <Toast.Viewport className="fixed bottom-0 right-0 z-[100] flex w-96 max-w-[100vw] flex-col gap-2 p-4 outline-none" />
    </Toast.Provider>
  )
}
